import { Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import {
  CampaignProduct,
  StepEditorIds,
  StepElements,
  StepTextual,
  StepTextualLayout,
  CustomStepCategoryEnum,
  WidgetVariable,
  buildPathVariables,
  StepEmulatorDevices,
  StepSectionWidths,
  DiscountSetting,
} from '../../../_models';
import { FormControl, FormGroup } from '@angular/forms';
import { Form, FormControlStatus } from '../../../_forms';
import { AlertService, StepEmulatorService, WidgetService } from '../../../_services';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { Subscription } from 'rxjs';
import {
  addDummyValues,
  descriptionExcludedStepTypes,
  enableImagePaste,
  FontFamilyFormats,
  FontSizes,
  initializeEditorStyles,
  StepHints,
} from '../../step-util';
import { trigger, state, style, animate, transition } from '@angular/animations';
import * as tinymce from 'tinymce';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'step-textual',
  templateUrl: './step-textual.component.html',
  styleUrls: ['./step-textual.component.scss'],
  animations: [
    trigger('fadeIn', [
      state('void', style({ opacity: 0 })),
      transition(':enter', [animate('500ms ease-in', style({ opacity: 1 }))]),
    ]),
  ],
})
export class StepTextualComponent extends Form implements OnInit, OnDestroy, OnChanges {
  @Input() form: FormGroup;
  @Input() enableFormErrors: boolean;
  @Input() layout: StepTextualLayout;
  @Input() editTemplate: boolean;
  @Input() templateId: string = '';
  @Input() stepId: string = '';
  @Input() product: CampaignProduct = {} as CampaignProduct;
  @Input() stepType: CustomStepCategoryEnum;
  @Input() isSurveyStep: boolean;
  @Input() isSingleStepOffer: boolean = false;
  @Input() activeDevice: StepEmulatorDevices;
  @Input() discount: DiscountSetting;
  @Input() theme: string;
  stepTextual: StepTextual;
  stepEditorIds = StepEditorIds;
  stepElements = StepElements;
  stepHints = StepHints;
  private subscription: Subscription;
  funnelPathVariables: { value: string; text: string }[] = [];
  bannerEditor: any;
  stepSectionWidths = StepSectionWidths;

  labelEditor = {
    plugins: 'line-height emoticons link paste autoresize semibold background-color rounded-corners swatch-colors',
    toolbar:
      'bold semibold italic underline strikethrough link | ' +
      'fontsizeselect fontselect lineheightselect| swatch-forecolor swatch-backcolor | ' +
      'background-color rounded-corners | alignleft aligncenter alignright | emoticons',
    fontsize_formats: FontSizes,
    font_formats: FontFamilyFormats,
    paste_as_text: true,
    auto_focus: this.stepEditorIds.Label,
    min_height: 120,
    max_height: 200,
    autoresize_bottom_margin: 0,
    setup: (editor: tinymce.Editor) => {
      initializeEditorStyles(editor, '24px', "'Open Sans', Ubuntu, sans-serif", true);

      editor.on('Change KeyUp', () => {
        const content = editor.getContent();
        this.onLabelChange(content);
      });
    },
  };

  subtitleEditor = {
    plugins: 'line-height emoticons link paste autoresize semibold background-color rounded-corners swatch-colors',
    toolbar:
      'bold semibold italic underline strikethrough link | ' +
      'fontsizeselect fontselect lineheightselect| swatch-forecolor swatch-backcolor | ' +
      'background-color rounded-corners | alignleft aligncenter alignright | emoticons',
    fontsize_formats: FontSizes,
    font_formats: FontFamilyFormats,
    paste_as_text: true,
    auto_focus: this.stepEditorIds.Subtitle,
    min_height: 120,
    max_height: 200,
    autoresize_bottom_margin: 0,
    setup: (editor: tinymce.Editor) => {
      initializeEditorStyles(editor, '24px', "'Open Sans', Ubuntu, sans-serif", true);

      editor.on('Change KeyUp', () => {
        const content = editor.getContent();
        this.onSubtitleChange(content);
      });
    },
  };

  contentEditor = {
    plugins:
      'lists line-height emoticons image link paste autoresize bullist numlist semibold background-color rounded-corners swatch-colors',
    toolbar:
      'bold semibold italic underline strikethrough link | fontsizeselect fontselect lineheightselect | bullist numlist | ' +
      'swatch-forecolor swatch-backcolor | background-color rounded-corners | alignleft aligncenter alignright | image | emoticons',
    fontsize_formats: FontSizes,
    font_formats: FontFamilyFormats,
    paste_as_text: true,
    auto_focus: this.stepEditorIds.Label,
    min_height: 200,
    max_height: 500,
    autoresize_bottom_margin: 0,
    setup: (editor: tinymce.Editor) => {
      initializeEditorStyles(editor, '22px', "'Open Sans', Ubuntu, sans-serif", true);
      enableImagePaste(editor);

      editor.on('Change KeyUp', () => {
        const content = editor.getContent();
        this.onContentChange(content);
      });
    },
  };

  constructor(
    protected location: Location,
    protected alertService: AlertService,
    protected stepEmulatorService: StepEmulatorService,
    protected router: Router,
    protected widgetService: WidgetService
  ) {
    super(alertService, router, location);
  }

  ngOnInit() {
    this.subscription = this.stepEmulatorService.stepTextual$.subscribe((data) => {
      this.stepTextual = data;
      this.initBannerEditor();
    });

    this.widgetService
      .getProductWidgetVariables()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (widgets: WidgetVariable[]) => {
          this.funnelPathVariables = buildPathVariables(widgets, {
            supportPhone: this.stepType === CustomStepCategoryEnum.ScheduleSupport,
          });
          this.initalizeValues();
        },
        (error) => {
          this.handleError(error);
          this.initalizeValues();
        }
      );
  }

  ngOnChanges(changes: SimpleChanges) {
    if ((changes.product && this.product) || (changes.discount && this.discount)) {
      this.onBannerChange(this.form.value.banner);
      this.onLabelChange(this.form.value.label);
      this.onSubtitleChange(this.form.value.subtitle);
      this.onContentChange(this.form.value.content);
    }

    if (changes.theme && this.theme) {
      this.refreshBannerThemeBackground();
    }
  }

  initalizeValues() {
    this.stepTextual.banner = addDummyValues(
      this.form.get('banner').value,
      this.funnelPathVariables,
      this.product,
      this.discount
    );
    this.stepTextual.body.label = addDummyValues(
      this.form.get('label').value,
      this.funnelPathVariables,
      this.product,
      this.discount
    );
    this.stepTextual.body.subtitle = addDummyValues(
      this.form.get('subtitle').value,
      this.funnelPathVariables,
      this.product,
      this.discount
    );
    this.stepTextual.body.content = addDummyValues(
      this.form.get('content').value,
      this.funnelPathVariables,
      this.product,
      this.discount
    );
  }

  toggleElement(element: StepElements) {
    this.stepEmulatorService.toggleElement(element);
  }

  isElementVisible(element: StepElements): boolean {
    return this.stepEmulatorService.isElementVisible(element);
  }

  private initBannerEditor() {
    this.bannerEditor = {
      plugins: 'line-height emoticons paste autoresize background-color swatch-colors',
      toolbar:
        'bold italic underline strikethrough | fontsizeselect fontselect lineheightselect| swatch-forecolor | background-color | ' +
        'alignleft aligncenter alignright | emoticons',
      fontsize_formats: FontSizes,
      font_formats: FontFamilyFormats,
      paste_as_text: true,
      min_height: 80,
      max_height: 150,
      autoresize_bottom_margin: -10,
      setup: (editor: tinymce.Editor) => {
        initializeEditorStyles(editor, '16px', "'Open Sans', Ubuntu, sans-serif", true);

        // Default the banner background to the campaign theme color when none is set
        editor.on('init blur', () => {
          this.ensureBannerThemeBackground();
        });

        editor.on('Change KeyUp', () => {
          const content = editor.getContent();
          this.onBannerChange(content);
          this.ensureBannerThemeBackground();
        });
      },
    };
  }

  private bannerContentHasBackground(content: string): boolean {
    return /background-color\s*:/i.test(content);
  }

  // Default the banner background to the theme color while it has no color of its own, including when still empty
  ensureBannerThemeBackground() {
    const editor = tinymce.get(this.stepEditorIds.Banner);
    if (editor && this.theme) {
      const content = editor.getContent();
      const hasContent = !!(content && content.trim());
      const unset = !this.bannerContentHasBackground(content);
      if (unset && (!(editor as any).autoTextColor || hasContent)) {
        // Preserve the caret so styling the text while typing doesn't jump the cursor
        const bookmark = (editor as any).hasFocus() ? editor.selection.getBookmark(2) : null;
        editor.execCommand('mceThemeBackgroundColor', false, this.theme);
        if (bookmark) {
          editor.selection.moveToBookmark(bookmark);
        }
      }
    }
  }

  // Keep the theme-applied banner color in sync once the real theme resolves; never touch a manually picked one
  refreshBannerThemeBackground() {
    const editor = tinymce.get(this.stepEditorIds.Banner);
    if (editor && this.theme && (editor as any).autoTextColor) {
      editor.execCommand('mceThemeBackgroundColor', false, this.theme);
    }
  }

  updateEditorBackgrounds(color: string) {
    const editor = tinymce.get(this.stepEditorIds.Banner);
    if (editor) {
      editor.execCommand('mceBackgroundColor', false, color);
    }
  }

  updateContentEditorBackgrounds(color: string) {
    const editor = tinymce.get(this.stepEditorIds.Content);
    if (editor) {
      editor.execCommand('mceBackgroundColor', false, color);
    }
  }

  updateContentEditorRoundedCorners(state: boolean) {
    const editor = tinymce.get(this.stepEditorIds.Content);
    if (editor) {
      editor.execCommand('mceRoundedCorners', false, state);
    }
  }

  updateSubtitleEditorBackgrounds(color: string) {
    const editor = tinymce.get(this.stepEditorIds.Subtitle);
    if (editor) {
      editor.execCommand('mceBackgroundColor', false, color);
    }
  }

  updateSubtitleEditorRoundedCorners(state: boolean) {
    const editor = tinymce.get(this.stepEditorIds.Subtitle);
    if (editor) {
      editor.execCommand('mceRoundedCorners', false, state);
    }
  }

  updateLabelEditorBackgrounds(color: string) {
    const editor = tinymce.get(this.stepEditorIds.Label);
    if (editor) {
      editor.execCommand('mceBackgroundColor', false, color);
    }
  }

  updateLabelEditorRoundedCorners(state: boolean) {
    const editor = tinymce.get(this.stepEditorIds.Label);
    if (editor) {
      editor.execCommand('mceRoundedCorners', false, state);
    }
  }

  onBannerChange(content: string) {
    if (this.stepTextual) {
      const updatedContent = addDummyValues(content, this.funnelPathVariables, this.product, this.discount);
      this.stepTextual.banner = updatedContent;
    }
  }

  onLabelChange(content: string) {
    if (this.stepTextual) {
      const updatedContent = addDummyValues(content, this.funnelPathVariables, this.product, this.discount);
      this.stepTextual.body.label = updatedContent;
    }
  }

  onSubtitleChange(content: string) {
    if (this.stepTextual) {
      const updatedContent = addDummyValues(content, this.funnelPathVariables, this.product, this.discount);
      this.stepTextual.body.subtitle = updatedContent;
    }
  }

  onContentChange(content: string) {
    if (this.stepTextual) {
      const updatedContent = addDummyValues(content, this.funnelPathVariables, this.product, this.discount);
      this.stepTextual.body.content = updatedContent;
    }
  }

  addBannerVariable(event: any) {
    const selectElement = event.target as HTMLSelectElement;
    const selectedValue = event.target.value;
    const editorInstance = tinymce.get(this.stepEditorIds.Banner);
    if (editorInstance) {
      editorInstance.insertContent(selectedValue);
    }

    selectElement.selectedIndex = 0;
  }

  addLabelVariable(event: any) {
    const selectElement = event.target as HTMLSelectElement;
    const selectedValue = event.target.value;
    const editorInstance = tinymce.get(this.stepEditorIds.Label);
    if (editorInstance) {
      editorInstance.insertContent(selectedValue);
    }

    selectElement.selectedIndex = 0;
  }

  addSubtitleVariable(event: any) {
    const selectElement = event.target as HTMLSelectElement;
    const selectedValue = event.target.value;
    const editorInstance = tinymce.get(this.stepEditorIds.Subtitle);
    if (editorInstance) {
      editorInstance.insertContent(selectedValue);
    }

    selectElement.selectedIndex = 0;
  }

  addContentVariable(event: any) {
    const selectElement = event.target as HTMLSelectElement;
    const selectedValue = event.target.value;

    const editorInstance = tinymce.get(this.stepEditorIds.Content);
    if (editorInstance) {
      editorInstance.insertContent(selectedValue);
    }

    selectElement.selectedIndex = 0;
  }

  get nameInvalidity() {
    return this.enableFormErrors && this.form.controls.name.status === FormControlStatus.Invalid;
  }

  get labelInvalidity() {
    return this.enableFormErrors && this.form.controls.label.status === FormControlStatus.Invalid;
  }

  get subtitleInvalidity() {
    return this.enableFormErrors && this.form.controls.subtitle && this.form.controls.subtitle.status === FormControlStatus.Invalid;
  }

  get contentInvalidity() {
    return this.enableFormErrors && this.form.controls.content.status === FormControlStatus.Invalid;
  }

  getWidthControl(type: StepSectionWidths.Label | StepSectionWidths.Subtitle | StepSectionWidths.Content): FormControl {
    const deviceKey = this.activeDevice.toString();
    return this.form.get('content_config').get(type).get(deviceKey) as FormControl;
  }

  isHidden(type: StepSectionWidths): boolean {
    return this.form.get('content_config').get(type).get('is_hidden').value;
  }

  onToggleChange(event: Event, type: StepSectionWidths) {
    const checked = (event.target as HTMLInputElement).checked;
    this.form.get('content_config').get(type).get('is_hidden').setValue(!checked);
  }

  get isDescriptionExcludedStepType(): boolean {
    return descriptionExcludedStepTypes.includes(this.stepType) && this.isSingleStepOffer;
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }
}
